import {
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  formatDateTime,
  type DataTableColumn,
} from '@berky/ui'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import {
  type HomepageNews,
  type HomepageNewsStatisticsPage,
  type HomepageNewsUserStatistic,
  useNotificationsApi,
} from './api'

const PAGE_SIZE = 25

const columns: DataTableColumn<HomepageNewsUserStatistic>[] = [
  {
    key: 'user',
    header: 'Nutzer',
    cell: (row) => (
      <div>
        <p className="font-medium">{row.name}</p>
        <p className="text-xs text-muted-foreground">{row.email ?? row.userId}</p>
      </div>
    ),
  },
  {
    key: 'count',
    header: 'Anzeigen',
    align: 'right',
    cell: (row) => <span data-numeric>{row.presentationCount}</span>,
  },
  {
    key: 'lastPresentedAt',
    header: 'Zuletzt angezeigt',
    cell: (row) => formatDateTime(row.lastPresentedAt),
  },
]

export function HomepageNewsStatisticsDialog({
  news,
  onOpenChange,
}: {
  news: HomepageNews | null
  onOpenChange: (open: boolean) => void
}) {
  const api = useNotificationsApi()
  const [page, setPage] = useState(1)

  function handleOpenChange(open: boolean) {
    if (!open) {
      setPage(1)
    }

    onOpenChange(open)
  }

  const statistics = useQuery({
    queryKey: ['notifications', 'homepage-news', news?.id, 'statistics', page],
    queryFn: ({ signal }) =>
      api.get<HomepageNewsStatisticsPage>(`/homepage-news/${news!.id}/statistics`, {
        query: { page, pageSize: PAGE_SIZE },
        signal,
      }),
    enabled: news !== null,
  })

  return (
    <Dialog open={news !== null} onOpenChange={handleOpenChange}>
      {news && (
        <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Nutzerstatistik</DialogTitle>
            <DialogDescription>
              {news.title}. Eine Anzeige wird gezählt, sobald das Popup erfolgreich an den
              Browser ausgeliefert wurde.
            </DialogDescription>
          </DialogHeader>

          {statistics.data && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Statistic label="Erreichte Nutzer" value={statistics.data.reachedUsers} />
              <Statistic
                label="Anzeigen insgesamt"
                value={statistics.data.totalPresentations}
              />
              <Statistic
                label="Letzte Anzeige"
                value={
                  statistics.data.lastPresentedAt
                    ? formatDateTime(statistics.data.lastPresentedAt)
                    : '–'
                }
              />
            </div>
          )}

          <DataTable
            columns={columns}
            rows={statistics.data?.items ?? []}
            rowKey={(row) => row.userId}
            caption={`Nutzerstatistik für ${news.title}`}
            isLoading={statistics.isPending}
            error={
              statistics.isError
                ? {
                    description: 'Die Nutzerstatistik konnte nicht geladen werden.',
                    details:
                      statistics.error instanceof Error
                        ? statistics.error.message
                        : undefined,
                    onRetry: () => statistics.refetch(),
                  }
                : null
            }
            pagination={
              statistics.data
                ? {
                    page: statistics.data.page,
                    pageSize: statistics.data.pageSize,
                    total: statistics.data.reachedUsers,
                  }
                : undefined
            }
            onPageChange={setPage}
          />
        </DialogContent>
      )}
    </Dialog>
  )
}

function Statistic({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold" data-numeric>
        {value}
      </p>
    </div>
  )
}
